import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loader } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Users, DoorOpen, ClipboardCheck, LogIn, Building2, GitBranch,
  CalendarClock, Clock, CheckCircle2, DoorClosed, Activity,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, AreaChart, Area } from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);

  if (!stats) return <Loader />;

  const cardsByRole = {
    SUPER_ADMIN: [
      { label: "Companies", value: stats.companies, icon: Building2, tone: "navy", t: "stat-companies" },
      { label: "Branches", value: stats.branches, icon: GitBranch, tone: "blue", t: "stat-branches" },
      { label: "Visitors Today", value: stats.visitors_today, icon: ClipboardCheck, tone: "green", t: "stat-visitors-today" },
      { label: "Active Visitors", value: stats.active_visitors, icon: Activity, tone: "amber", t: "stat-active-visitors" },
      { label: "Bookings Today", value: stats.bookings_today, icon: CalendarClock, tone: "navy", t: "stat-bookings-today" },
      { label: "Meeting Rooms", value: stats.meeting_rooms, icon: DoorOpen, tone: "blue", t: "stat-rooms" },
      { label: "Pending Approvals", value: (stats.pending_employee_approval || 0) + (stats.pending_extended_bookings || 0), icon: Clock, tone: "amber", t: "stat-pending" },
      { label: "Available Rooms", value: stats.available_rooms, icon: DoorOpen, tone: "green", t: "stat-available" },
    ],
    COMPANY_ADMIN: [
      { label: "Branches", value: stats.branches, icon: GitBranch, tone: "navy", t: "stat-branches" },
      { label: "Visitors Today", value: stats.visitors_today, icon: ClipboardCheck, tone: "green", t: "stat-visitors-today" },
      { label: "Active Visitors", value: stats.active_visitors, icon: Activity, tone: "amber", t: "stat-active-visitors" },
      { label: "Bookings Today", value: stats.bookings_today, icon: CalendarClock, tone: "blue", t: "stat-bookings-today" },
      { label: "Pending Extended", value: stats.pending_extended_bookings, icon: Clock, tone: "amber", t: "stat-pending-extended" },
      { label: "Booked Rooms", value: stats.booked_rooms, icon: DoorClosed, tone: "red", t: "stat-booked" },
    ],
    BRANCH_ADMIN: [
      { label: "Visitors Today", value: stats.visitors_today, icon: ClipboardCheck, tone: "green", t: "stat-visitors-today" },
      { label: "Pending Employee Approval", value: stats.pending_employee_approval, icon: Clock, tone: "amber", t: "stat-pending-emp" },
      { label: "Pending Verification", value: stats.pending_reception_verification, icon: CheckCircle2, tone: "blue", t: "stat-pending-verify" },
      { label: "Visitors Inside", value: stats.active_visitors, icon: Activity, tone: "navy", t: "stat-inside" },
      { label: "Available Rooms", value: stats.available_rooms, icon: DoorOpen, tone: "green", t: "stat-available" },
      { label: "Pending Extended", value: stats.pending_extended_bookings, icon: CalendarClock, tone: "amber", t: "stat-pending-extended" },
    ],
    RECEPTIONIST: [
      { label: "Pending Verification", value: stats.pending_reception_verification, icon: CheckCircle2, tone: "amber", t: "stat-pending-verify" },
      { label: "Verified Today", value: stats.verified_today, icon: ClipboardCheck, tone: "green", t: "stat-verified" },
      { label: "Visitors Inside", value: stats.active_visitors, icon: Activity, tone: "blue", t: "stat-inside" },
      { label: "Checked Out", value: stats.checked_out_today, icon: DoorClosed, tone: "navy", t: "stat-checkout" },
      { label: "Available Rooms", value: stats.available_rooms, icon: DoorOpen, tone: "green", t: "stat-available" },
    ],
  };
  const cards = cardsByRole[user.role] || [];

  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle="Here's what's happening across your workspace today." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => <StatCard key={c.label} {...c} testid={c.t} />)}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 sf-fade-up">
          <h3 className="font-display font-semibold text-navy-900 mb-4">Visitor Trend · Last 7 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.visitor_trend || []}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip />
              <Area type="monotone" dataKey="visitors" stroke="#16305B" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sf-fade-up">
          <h3 className="font-display font-semibold text-navy-900 mb-4">Recent Activity</h3>
          <div className="space-y-3 max-h-[260px] overflow-y-auto sf-scroll">
            {(stats.recent_activity || []).length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
            {(stats.recent_activity || []).map((a) => (
              <div key={a.id} className="flex items-start gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-navy-900 font-medium truncate">{a.action.replace(/_/g, " ")}</p>
                  <p className="text-slate-400 text-xs">{a.user_name} · {new Date(a.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
